// src/services/maps/clustering.js
// Marker layer management with automatic clustering.
// Uses plain LayerGroup per type. leaflet.markercluster is not installed;
// pin volume is controlled by hard caps (150/50) instead.
//
// Priority rules:
//   SOS (priority=5) — never clustered, always visible, red halo pulse
//   Emergency (priority=4) — clustered with red badge
//   Standard (priority 1-3) — clustered with amber badge
//
// One LayerGroup per data type for independent show/hide.

import L from 'leaflet';
import { ZOOM_THRESHOLDS, CLUSTER_CONFIG, MARKER_COLORS } from './config.js';
import { ICONS, buildPopup } from './leafletCore.js';
import { mapsDirectionsUrl } from '@services/geolocation';

// ── Layer groups ──────────────────────────────────────────────────
// Each domain has its own group so modes can toggle them independently.
const _groups = {
  incidents:    null,
  shelters:     null,
  gas:          null,
  hospitals:    null,
  gauges:       null,
  sos:          null,
  connectivity: null,  // WiFi, charging, Starlink, LTE
};

// Cluster groups (one per domain — loaded lazily)
const _clusters = {};

// ── Init ──────────────────────────────────────────────────────────
// Call once after MapManager.init(). The map is passed in.
export function initLayers(map) {
  // Destroy existing layers
  destroyAllLayers(map);

  // SOS: raw LayerGroup, never clustered
  _groups.sos = L.layerGroup().addTo(map);

  // Everything else: try to use MarkerClusterGroup if available,
  // fall back to plain LayerGroup if the plugin isn't installed.
  for (const key of ['incidents', 'shelters', 'gas', 'hospitals', 'gauges', 'connectivity']) {
    _groups[key] = _makeClusterGroup(key);
    _groups[key].addTo(map);
  }
}

// ── Supercluster — client-side spatial clustering ─────────────────
// supercluster is a fast, lightweight clustering library that works
// without any Leaflet plugin. It runs the clustering computation in
// JS and returns cluster points we render as styled circle markers.
//
// Why supercluster over leaflet.markercluster:
//   - No plugin installation — pure JS, tree-shakeable
//   - 60fps: computation is O(n log n) on load, O(1) per viewport query
//   - Works correctly with our hard pin cap — we cluster the capped set
//   - Zoom-aware: clusters split/merge smoothly as user zooms

let _supercluster = null;       // lazily loaded
let _scIndex      = null;       // supercluster index for current incident set
let _scPoints     = null;       // the points array that built _scIndex (for change detection)

async function _getSupercluster() {
  if (_supercluster) return _supercluster;
  try {
    _supercluster = (await import('supercluster')).default;
  } catch {
    _supercluster = null;
  }
  return _supercluster;
}

// Build or reuse the supercluster index for this set of incidents
async function _buildIndex(incidents) {
  if (_scPoints === incidents) return _scIndex;   // same reference — reuse
  const SC = await _getSupercluster();
  if (!SC) return null;

  const index = new SC({ radius: 60, maxZoom: 16, minZoom: 1 });
  index.load(incidents.map(inc => ({
    type:       'Feature',
    geometry:   { type: 'Point', coordinates: [inc.lon, inc.lat] },
    properties: { ...inc },
  })));
  _scIndex  = index;
  _scPoints = incidents;
  return index;
}

// ── Cluster-aware incident rendering ─────────────────────────────
// Replaces renderIncidents with supercluster-powered grouping.
// Falls back to plain markers if supercluster fails to load.
export async function renderIncidentsClustered(map, incidents = [], onSelect, zoom = 12) {
  const group = _groups.incidents;
  if (!group) return;
  group.clearLayers();
  if (incidents.length === 0) return;

  const index = await _buildIndex(incidents);
  if (!index) {
    // supercluster unavailable — fall through to plain rendering
    renderIncidents(map, incidents, onSelect);
    return;
  }

  const bounds  = map.getBounds();
  const bbox    = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()];
  const zoomInt = Math.round(zoom);
  const clusters = index.getClusters(bbox, zoomInt);

  for (const feature of clusters) {
    const [lon, lat] = feature.geometry.coordinates;
    const props      = feature.properties;

    if (props.cluster) {
      // ── Cluster circle ─────────────────────────────────────────
      const count    = props.point_count;
      const maxSev   = props.point_count_abbreviated;  // largest sev in cluster
      const color    = count > 20  ? '#dc2626'         // red — many incidents
                     : count > 5   ? '#d97706'         // amber — several
                     :               '#0369a1';         // blue — small cluster
      const size     = count > 50 ? 44 : count > 10 ? 38 : 32;

      const icon = L.divIcon({
        className: '',
        html: `<div style="
          width:${size}px;height:${size}px;border-radius:50%;
          background:${color};border:2px solid rgba(255,255,255,0.8);
          display:flex;align-items:center;justify-content:center;
          font-size:${size > 38 ? 13 : 11}px;font-weight:900;
          color:#fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);
          cursor:pointer;">${count}</div>`,
        iconSize:   [size, size],
        iconAnchor: [size / 2, size / 2],
      });

      const marker = L.marker([lat, lon], { icon });
      // Clicking a cluster zooms to expand it
      marker.on('click', () => {
        const expansionZoom = Math.min(
          index.getClusterExpansionZoom(props.cluster_id),
          18
        );
        map.flyTo([lat, lon], expansionZoom, { duration: 0.4 });
      });
      group.addLayer(marker);

    } else {
      // ── Individual marker (same as renderIncidents) ────────────
      const inc     = props;
      const iconKey = inc.type === 'sos'          ? 'sos'
                    : inc.type === 'road_flooding' ? 'flood'
                    : inc.type === 'road_closure'  ? 'road'
                    : inc.type === 'power_outage'  ? 'power'
                    : inc.type === 'fire'          ? 'fire'
                    : 'road';
      if (!lat || !lon) continue;
      const icon   = ICONS[iconKey]?.() ?? ICONS.road();
      const marker = L.marker([lat, lon], { icon, _priority: inc.priority ?? 2 });
      marker.bindPopup(buildPopup({
        title:     inc.title,
        subtitle:  inc.roadName ?? inc.crossStreet ?? '',
        body:      inc.description ?? '',
        color:     MARKER_COLORS[inc.type] ?? MARKER_COLORS.default,
        directionsUrl: mapsDirectionsUrl(lat, lon),
      }));
      if (onSelect) marker.on('click', () => onSelect(inc));
      group.addLayer(marker);
    }
  }
}

function _makeClusterGroup(_key) {
  // Plain LayerGroup — supercluster handles the cluster logic above.
  return L.layerGroup();
}

// ── Render functions ──────────────────────────────────────────────
// Each render function clears its group and re-populates.
// Called by the map hook whenever data changes.

export function renderIncidents(map, incidents = [], onSelect) {
  const group = _groups.incidents;
  if (!group) return;
  group.clearLayers();

  for (const inc of incidents) {
    const iconKey = inc.type === 'sos'          ? 'sos'
                  : inc.type === 'road_flooding' ? 'flood'
                  : inc.type === 'road_closure'  ? 'road'
                  : inc.type === 'power_outage'  ? 'power'
                  : inc.type === 'fire'          ? 'fire'
                  : 'road';

    if (!inc.lat || !inc.lon) continue;

    const icon    = ICONS[iconKey]?.() ?? ICONS.road();
    const marker  = L.marker([inc.lat, inc.lon], { icon, _priority: inc.priority ?? 2 });

    marker.bindPopup(buildPopup({
      title:     inc.title,
      subtitle:  inc.roadName ?? inc.crossStreet ?? '',
      body:      inc.description ?? '',
      color:     MARKER_COLORS[inc.type] ?? MARKER_COLORS.default,
      directionsUrl: inc.lat ? mapsDirectionsUrl(inc.lat, inc.lon) : null,
    }));

    if (onSelect) marker.on('click', () => onSelect(inc));
    group.addLayer(marker);
  }
}

export function renderSOS(map, reports = [], onSelect) {
  const group = _groups.sos;
  if (!group) return;
  group.clearLayers();

  for (const r of reports) {
    if (!r.lat || !r.lon) continue;

    // SOS markers pulse — implemented with a CSS class on a DivIcon
    const icon = L.divIcon({
      html: `<div class="na-sos-pulse">🚨</div>`,
      className:   '',
      iconSize:    [40, 40],
      iconAnchor:  [20, 20],
      popupAnchor: [0, -24],
    });

    const marker = L.marker([r.lat, r.lon], { icon, zIndexOffset: 9999, _priority: 5 });

    marker.bindPopup(buildPopup({
      title:   'SOS — ' + (r.sosSubtype ?? 'Emergency'),
      body:    r.body?.substring(0, 200) ?? '',
      callUrl: 'tel:911',
      color:   MARKER_COLORS.sos,
    }));

    if (onSelect) marker.on('click', () => onSelect(r));
    group.addLayer(marker);
  }
}

export function renderShelters(map, shelters = [], onSelect) {
  const group = _groups.shelters;
  if (!group) return;
  group.clearLayers();

  for (const s of shelters) {
    if (!s.lat || !s.lon) continue;
    const iconKey = s.status === 'open' ? 'shelter_open'
                  : s.status === 'at_capacity' ? 'shelter_full'
                  : 'shelter_closed';
    const marker = L.marker([s.lat, s.lon], { icon: ICONS[iconKey](), _priority: 2 });

    marker.bindPopup(buildPopup({
      title:     s.name,
      subtitle:  s.status === 'open' ? 'Open' : s.status === 'at_capacity' ? 'At Capacity' : 'Closed',
      body:      [
        s.acceptsPets   ? 'Pets welcome'    : '',
        s.isADAAccessible ? 'ADA accessible' : '',
        s.hasFood       ? 'Food available'  : '',
        s.hasCots       ? 'Cots available'  : '',
      ].filter(Boolean).join(' · '),
      callUrl:        s.phone ? `tel:${s.phone}` : null,
      directionsUrl:  mapsDirectionsUrl(s.lat, s.lon, s.name),
      color:          MARKER_COLORS.shelter_open,
    }));

    if (onSelect) marker.on('click', () => onSelect(s));
    group.addLayer(marker);
  }
}

export function renderGasStations(map, stations = [], onSelect) {
  const group = _groups.gas;
  if (!group) return;
  group.clearLayers();

  for (const s of stations) {
    if (!s.lat || !s.lon) continue;
    const iconKey = s.status === 'open'   ? 'gas_open'
                  : s.status === 'busy'   ? 'gas_busy'
                  : 'gas_closed';
    const marker = L.marker([s.lat, s.lon], { icon: ICONS[iconKey](), _priority: 1 });

    const waitLabel = s.waitMinutes === 0 ? 'No wait'
                    : s.waitMinutes      ? `~${s.waitMinutes}m wait`
                    : '';

    marker.bindPopup(buildPopup({
      title:   s.name,
      body:    [
        s.priceRegular ? `$${s.priceRegular.toFixed(2)}/gal` : '',
        waitLabel,
        s.fuelTypes?.join(', ') ?? '',
      ].filter(Boolean).join(' · '),
      directionsUrl: mapsDirectionsUrl(s.lat, s.lon, s.name),
      color:         MARKER_COLORS.gas_open,
    }));

    if (onSelect) marker.on('click', () => onSelect(s));
    group.addLayer(marker);
  }
}

export function renderUSGSGauges(map, gauges = [], onSelect) {
  const group = _groups.gauges;
  if (!group) return;
  group.clearLayers();

  // Gauge lat/lon comes from LOW_WATER_CROSSINGS in static.js — pair by gaugeId
  for (const g of gauges) {
    if (!g.lat || !g.lon) continue;
    const iconKey = g.ft > 15 ? 'gauge_flood'
                  : g.ft > 8  ? 'gauge_alert'
                  : 'gauge_normal';
    const marker = L.marker([g.lat, g.lon], { icon: ICONS[iconKey](), _priority: 2 });

    marker.bindPopup(buildPopup({
      title:  g.name,
      body:   `${g.ft.toFixed(1)} ft — ${g.stage}`,
      color:  g.color,
    }));

    if (onSelect) marker.on('click', () => onSelect(g));
    group.addLayer(marker);
  }
}

export function renderHospitals(map, hospitals = [], onSelect) {
  const group = _groups.hospitals;
  if (!group) return;
  group.clearLayers();

  for (const h of hospitals) {
    const marker = L.marker([h.lat, h.lon], { icon: ICONS.hospital(), _priority: 2 });

    marker.bindPopup(buildPopup({
      title:         h.shortName,
      subtitle:      h.er24 ? 'ER Open 24/7' : h.hours ?? '',
      body:          h.services?.slice(0, 2).join(', ') ?? '',
      callUrl:       h.phone ? `tel:${h.phone}` : null,
      directionsUrl: mapsDirectionsUrl(h.lat, h.lon, h.name),
      color:         '#0369a1',
    }));

    if (onSelect) marker.on('click', () => onSelect(h));
    group.addLayer(marker);
  }
}

// ── Show / hide individual groups ────────────────────────────────
export function showGroup(map, key) {
  if (_groups[key] && !map.hasLayer(_groups[key])) {
    _groups[key].addTo(map);
  }
}

export function hideGroup(map, key) {
  if (_groups[key] && map.hasLayer(_groups[key])) {
    map.removeLayer(_groups[key]);
  }
}

export function setVisibleGroups(map, keys = []) {
  for (const key of Object.keys(_groups)) {
    keys.includes(key) ? showGroup(map, key) : hideGroup(map, key);
  }
}

// ── Destroy ───────────────────────────────────────────────────────
export function renderConnectivity(map, points = [], onSelect) {
  const group = _groups.connectivity;
  if (!group) return;
  group.clearLayers();

  for (const pt of points) {
    if (!pt.lat || !pt.lon) continue;

    // Pick icon: type + status combination
    const iconKey = (() => {
      const s = pt.status;
      if (pt.type === 'wifi_shelter')   return 'wifi_shelter';
      if (pt.type === 'wifi_public')    return s === 'offline' ? 'wifi_offline' : s === 'active' ? 'wifi_active' : 'wifi_likely';
      if (pt.type === 'charging')       return s === 'active' ? 'charging_active' : 'charging_likely';
      if (pt.type === 'starlink')       return s === 'active' ? 'starlink_active' : 'starlink_likely';
      if (pt.type === 'open_business')  return 'open_business';
      if (pt.type === 'lte_recovery')   return 'lte_recovery';
      return 'wifi_likely';
    })();

    const icon   = ICONS[iconKey]?.() ?? ICONS.wifi_likely();
    const marker = L.marker([pt.lat, pt.lon], { icon, _priority: pt.type === 'wifi_shelter' ? 3 : 1 });

    const statusLine = pt.status === 'active' ? '✓ Confirmed Active'
                     : pt.status === 'likely' ? '⚠ Likely Available'
                     : pt.status === 'offline'? '✗ Offline'
                     : '? Status Unknown';

    const detailLines = [
      pt.speedMbps    ? `Speed: ${pt.speedMbps} Mbps`            : null,
      pt.ports        ? `Ports: ${pt.ports}`                      : null,
      pt.powerWatts   ? `Power: ${pt.powerWatts}W per port`       : null,
      pt.radiusMiles  ? `Coverage: ~${pt.radiusMiles} mi radius`  : null,
      pt.hours        ? `Hours: ${pt.hours}`                      : null,
      pt.requiresLogin? 'Login required'                          : null,
      pt.notes        ? pt.notes.substring(0, 120)                : null,
    ].filter(Boolean);

    marker.bindPopup(buildPopup({
      title:        pt.name,
      subtitle:     statusLine,
      body:         detailLines.join(' · '),
      directionsUrl: `https://www.google.com/maps/dir/?api=1&destination=${pt.lat},${pt.lon}`,
    }));

    if (onSelect) marker.on('click', () => onSelect(pt));
    group.addLayer(marker);
  }
}

export function destroyAllLayers(map) {
  for (const [key, group] of Object.entries(_groups)) {
    if (group && map) map.removeLayer(group);
    _groups[key] = null;
  }
}
